// public/js/modules/ui/themeManager.js

import { applyBanner } from '../../components/customization/bannerManager.js';
import { applyBackground } from '../../components/customization/bannerManager.js';
import { setShadingLevel, setGridOpacity } from './shadingManager.js';
import { COLOR_TARGET_MAP, LEGACY_COLOR_KEY_MAP } from './colorData.js';
import { getDefaultColorScheme } from '../firestore/firestoreCustomization.js';
import { updateAllTextContrast, generateTextColors, setTextColorVariables } from './luminanceUtils.js';


let isInitializing = false;


let themeDiscardedFlag = false;


let isApplyingTheme = false;
let lastAppliedThemeJSON = null;
let emitRetryPending = false;
let emitRetryCount = 0;
const MAX_EMIT_RETRIES = 50;


const dawFallbackColors = {
    highlight1: '#dab8cc',
    tertiary: '#130202',
    main: '#0d0b0b',
    highlight2: '#55614e',
    secondary: '#231b07'
};


function resolveColorValue(rawValue, style) {
    if (!rawValue) return null;
    let v = rawValue.trim();
    if (!v || v === 'undefined') return null;

    for (let depth = 0; depth < 3 && v.startsWith('var('); ++depth) {
        const match = v.match(/^var\(([^,)]+)(?:,\s*(.+))?\)$/);
        if (!match) return null;
        const varName = match[1].trim();
        const fallback = match[2] ? match[2].trim() : '';
        const resolved = style.getPropertyValue(varName).trim();
        v = resolved || fallback;
        if (!v || v === 'undefined') return null;
    }

    const probe = document.createElement('div');
    probe.style.position = 'fixed';
    probe.style.left = '-9999px';
    probe.style.color = v;
    document.body.appendChild(probe);
    const computed = getComputedStyle(probe).color;
    document.body.removeChild(probe);

    if (!computed || computed === 'rgba(0, 0, 0, 0)' || computed === 'transparent') return null;
    return computed;
}


let lastThemeColors = { ...dawFallbackColors };


function colorToRgb(value, fallback) {
    const style = getComputedStyle(document.documentElement);
    return resolveColorValue(value, style) || resolveColorValue(fallback, style) || fallback;
}


function emitFlurboTheme() {
    if (typeof window.__JUCE__ === 'undefined' || !window.__JUCE__?.backend?.emitEvent) {
        if (!emitRetryPending && emitRetryCount < MAX_EMIT_RETRIES) {
            emitRetryPending = true;
            emitRetryCount++;
            setTimeout(() => { emitRetryPending = false; emitFlurboTheme(); }, 100);
        }
        return;
    }

    emitRetryPending = false;
    emitRetryCount = 0;

    try {
        const values = {
            main:       colorToRgb(lastThemeColors.main, dawFallbackColors.main),
            highlight1: colorToRgb(lastThemeColors.highlight1, dawFallbackColors.highlight1),
            highlight2: colorToRgb(lastThemeColors.highlight2, dawFallbackColors.highlight2),
            secondary:  colorToRgb(lastThemeColors.secondary, dawFallbackColors.secondary),
            tertiary:   colorToRgb(lastThemeColors.tertiary, dawFallbackColors.tertiary)
        };
        window.__JUCE__.backend.emitEvent('flurboLog', '[ThemeManager] emitFlurboTheme: ' + JSON.stringify(values));
        window.__JUCE__.backend.emitEvent('flurboTheme', values);
    } catch (e) {}
}


export function applyFallbackThemeColors() {
    console.log('[Theme Manager] Applying fallback theme colors...');
    
    
    const fallbackColors = {
        highlight1: '#dab8cc',  
        tertiary: '#130202',     
        main: '#0d0b0b',         
        highlight2: '#55614e',  
        secondary: '#231b07'     
    };

    lastThemeColors = { ...fallbackColors };
    
    
    for (const [groupKey, value] of Object.entries(fallbackColors)) {
        const target = COLOR_TARGET_MAP[groupKey];
        if (target && value) {
            if (target.property) {
                document.documentElement.style.setProperty(target.property, value);
            }
            if (target.additionalProperties && Array.isArray(target.additionalProperties)) {
                target.additionalProperties.forEach(prop => {
                    document.documentElement.style.setProperty(prop, value);
                });
            }
        }
    }
    
    setTextColorVariables(generateTextColors(fallbackColors));
    updateAllTextContrast();
    emitFlurboTheme();
    console.log('[Theme Manager] Fallback theme colors applied');
}


export function setThemeInitializationMode(initing) {
    isInitializing = initing;
}


export function isInThemeInitializationMode() {
    return isInitializing;
}


export function clearThemeDiscardedFlag() {
    themeDiscardedFlag = false;
}


export function isThemeDiscarded() {
    return themeDiscardedFlag;
}


export function saveUnsavedThemeState() {
    const themeState = captureCurrentThemeState();
    try {
        localStorage.setItem('unsavedThemeState', JSON.stringify(themeState));
        console.log('[Theme Manager] Unsaved theme state saved to localStorage');
    } catch (error) {
        console.error('[Theme Manager] Failed to save unsaved theme state:', error);
    }
}


export function getUnsavedThemeState() {
    try {
        const saved = localStorage.getItem('unsavedThemeState');
        return saved ? JSON.parse(saved) : null;
    } catch (error) {
        console.error('[Theme Manager] Failed to retrieve unsaved theme state:', error);
        return null;
    }
}


export function clearUnsavedThemeState() {
    try {
        localStorage.removeItem('unsavedThemeState');
        console.log('[Theme Manager] Unsaved theme state cleared from localStorage');
    } catch (error) {
        console.error('[Theme Manager] Failed to clear unsaved theme state:', error);
    }
}


export function captureCurrentThemeState() {
    
    const headerBannerImg = document.getElementById('header-banner-img');
    const userBannerImg = document.getElementById('user-banner-img');
    const bannerImg = headerBannerImg || userBannerImg;
    let capturedBannerUrl = bannerImg ? bannerImg.getAttribute('src') : null;
    
    
    if (capturedBannerUrl && capturedBannerUrl.includes('placehold.co')) {
        capturedBannerUrl = null;
    }
    
    
    const splitContainer = document.getElementById('chat-split-container');
    let capturedBackgroundUrl = null;
    if (splitContainer) {
        const bgImage = splitContainer.style.backgroundImage;
        if (bgImage && bgImage !== 'none' && bgImage.includes('url(')) {
            
            const match = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
            if (match && match[1]) {
                capturedBackgroundUrl = match[1];
            }
        }
    }

    const themeData = {
        colors: {},
        bannerUrl: capturedBannerUrl,
        backgroundUrl: capturedBackgroundUrl,
        shadingLevel: parseInt(document.body.dataset.shadingLevel || '2', 10) || 2,
        timestamp: Date.now()
    };

    for (const [groupKey, target] of Object.entries(COLOR_TARGET_MAP)) {
        if (target && target.property) {
            const computedStyle = getComputedStyle(document.documentElement);
            const color = computedStyle.getPropertyValue(target.property).trim();

            if (color && color !== '' && color !== 'undefined') {
                themeData.colors[groupKey] = color;
            }
        }
    }

    themeData.textColors = generateTextColors(themeData.colors);

    return themeData;
}


export async function loadAndApplyTheme(userProfile) {
    
    if (isApplyingTheme) return;
    const incomingTheme = userProfile?.preferences?.activeScheme;
    const incomingThemeJSON = incomingTheme ? JSON.stringify(incomingTheme) : null;
    if (incomingThemeJSON && incomingThemeJSON === lastAppliedThemeJSON) return;

    isApplyingTheme = true;
    lastThemeColors = { ...dawFallbackColors };

    for (const target of Object.values(COLOR_TARGET_MAP)) {
        if (target.property)
            document.documentElement.style.removeProperty(target.property);
        if (target.additionalProperties && Array.isArray(target.additionalProperties))
            for (const p of target.additionalProperties)
                document.documentElement.style.removeProperty(p);
    }

    let theme = incomingTheme;

    if (!theme) {
        try {
            theme = await getDefaultColorScheme();
        } catch (error) {
            console.log('[Theme Manager] Error fetching default theme:', error.message || error.code || error);
        }
        if (!theme) {
            applyFallbackThemeColors();
            isApplyingTheme = false;
            return;
        }
    }

    
    if (theme && theme.colors) {
        for (const [groupKey, value] of Object.entries(theme.colors)) {
            const resolvedKey = COLOR_TARGET_MAP[groupKey] ? groupKey : LEGACY_COLOR_KEY_MAP[groupKey];
            const target = resolvedKey ? COLOR_TARGET_MAP[resolvedKey] : null;
            if (target && value) {
                lastThemeColors[resolvedKey] = value;
                if (target.property) {
                    document.documentElement.style.setProperty(target.property, value);
                }
                if (target.additionalProperties && Array.isArray(target.additionalProperties)) {
                    target.additionalProperties.forEach(prop => {
                        document.documentElement.style.setProperty(prop, value);
                    });
                }
            }
        }
    }
    
    
    
    const legacyColorScheme = userProfile?.preferences?.colorScheme;
    if (legacyColorScheme) {
        for (const [groupKey, value] of Object.entries(legacyColorScheme)) {
            const resolvedKey = COLOR_TARGET_MAP[groupKey] ? groupKey : LEGACY_COLOR_KEY_MAP[groupKey];
            const target = resolvedKey ? COLOR_TARGET_MAP[resolvedKey] : null;
            if (target && value) {
                lastThemeColors[resolvedKey] = value;
                if (target.property) {
                    document.documentElement.style.setProperty(target.property, value);
                }
                if (target.additionalProperties && Array.isArray(target.additionalProperties)) {
                    target.additionalProperties.forEach(prop => {
                        document.documentElement.style.setProperty(prop, value);
                    });
                }
            }
        }
    }
    
    
    if (theme) {
        if (theme.backgroundUrl) {
            applyBackground(theme.backgroundUrl);
        } else {
            applyBackground(null);
        }
    } else if (userProfile.chatBackgroundUrl) {
        applyBackground(userProfile.chatBackgroundUrl);
    } else {
        
        applyBackground(null);
    }
    
    
    const preferredFontName = userProfile?.preferences?.preferredFont_name;
    if (preferredFontName && preferredFontName !== 'system-ui') {
        
        const { loadGoogleFont } = await import('./fontManager.js');
        loadGoogleFont(preferredFontName.split(',')[0].replace(/'/g, '').trim());
        
        document.documentElement.style.setProperty('--font-name', preferredFontName);
    } else {
        
        document.documentElement.style.setProperty('--font-name', 'system-ui');
    }

    
    if (typeof theme.shadingLevel !== 'undefined') {
        setShadingLevel(theme.shadingLevel);
    }

    
    if (typeof theme.gridOpacity !== 'undefined') {
        setGridOpacity(theme.gridOpacity);
    }

    
    if (theme.isDefault) {
        const defaultToggle = document.getElementById('default-theme-toggle') || document.getElementById('default-theme-switch');
        if (defaultToggle) {
            if (defaultToggle.type === 'checkbox') defaultToggle.checked = true;
            else defaultToggle.classList.add('active');
        }
    }

    
    if (theme && theme.textColors) {
        setTextColorVariables(theme.textColors);
    }

    updateAllTextContrast();
    
    
    emitFlurboTheme();

    window.dispatchEvent(new CustomEvent('themeColorsChanged', { detail: { source: 'loadAndApplyTheme' } }));

    
    lastAppliedThemeJSON = theme ? JSON.stringify(theme) : null;
    isApplyingTheme = false;
}


export function updateColorSetting(groupKey, r, g, b) {
    const target = COLOR_TARGET_MAP[groupKey];
    if (!target) return;

    const colorValue = `rgb(${r}, ${g}, ${b})`;
    lastThemeColors[groupKey] = colorValue;
    
    if (target.property) {
        document.documentElement.style.setProperty(target.property, colorValue);
    }
    
    if (target.additionalProperties && Array.isArray(target.additionalProperties)) {
        target.additionalProperties.forEach(prop => {
            document.documentElement.style.setProperty(prop, colorValue);
        });
    }

    
    updateAllTextContrast();
    
    
    emitFlurboTheme();

    window.dispatchEvent(new CustomEvent('themeColorsChanged', { detail: { groupKey, colorValue } }));
}


export function recalculateTextContrast() {
    updateAllTextContrast();
}
